import { Button } from "@/components/ui/je-button"
import { useEffect, useState } from "react"
import { useLanguage } from "@/contexts/LanguageContext"
import { ArrowRight, ArrowUpRight } from "lucide-react"

const HeroSection = () => {
  const [currentWordIndex, setCurrentWordIndex] = useState(0)
  const { t } = useLanguage()

  const rotatingWords = [
    t('hero.words.software'),
    t('hero.words.ai'),
    t('hero.words.robotics'),
    t('hero.words.agriculture'),
    t('hero.words.architecture'),
    t('hero.words.mechanical'),
  ]

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduced) return
    const i = setInterval(() => setCurrentWordIndex((p) => (p + 1) % rotatingWords.length), 2400)
    return () => clearInterval(i)
  }, [rotatingWords.length])

  return (
    <section className="hero-modern pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden">
      <div className="container-editorial relative">
        <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
          {/* Headline */}
          <div className="col-span-12 lg:col-span-9">
            <h1 className="text-hero font-serif font-medium text-je-black text-balance">
              {t('hero.title')}{' '}
              <span className="relative inline-block">
                <span
                  key={currentWordIndex}
                  className="inline-block animate-fade-in italic text-je-red"
                  style={{ animationDuration: '0.5s' }}
                >
                  {rotatingWords[currentWordIndex]}
                </span>
                <span className="absolute -bottom-1 left-0 right-0 h-[3px] bg-je-red/15" />
              </span>
              <span className="text-je-navy/80">.</span>
            </h1>
          </div>

          {/* Subhead aligned right */}
          <div className="col-span-12 lg:col-span-3 lg:pl-6 lg:border-l lg:border-je-navy/15">
            <p className="text-base md:text-lg text-je-gray-dark leading-relaxed text-pretty">
              {t('hero.subtitle')} <span className="text-je-navy font-medium">{t('hero.subtitle2')}</span>
            </p>
          </div>
        </div>

        {/* CTA row */}
        <div className="mt-14 md:mt-20 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 animate-fade-in">
          <Button size="lg" asChild>
            <a href="#contact" className="gap-2">
              {t('nav.launchProject')}
              <ArrowRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-0.5" />
            </a>
          </Button>
          <Button variant="outline" size="lg" asChild>
            <a href="/rejoindre" className="gap-2">
              {t('nav.joinTeam')}
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </Button>

          {/* Trust line */}
          <div className="sm:ml-auto flex items-center gap-3 text-xs font-mono uppercase tracking-wider text-je-gray-dark">
            <span className="hidden sm:inline-block w-12 h-px bg-je-navy/20" />
            <span>9 disciplines · 100+ étudiants</span>
          </div>
        </div>
      </div>

      {/* Discipline marquee */}
      <div className="mt-20 md:mt-28 border-y border-je-navy/10 bg-je-white/40 overflow-hidden">
        <div className="marquee-track py-5">
          {[...Array(2)].flatMap((_, j) =>
            [
              'Informatique', 'Intelligence Artificielle', 'Génie Mécanique', 'Microtechnique',
              'Architecture', 'Génie Civil', 'Agronomie', 'Architecture du Paysage', 'Technique du Bâtiment',
            ].map((w, i) => (
              <span key={`${j}-${i}`} className="flex items-center gap-8 px-8 font-serif text-2xl md:text-3xl text-je-navy/70 whitespace-nowrap">
                {w}
                <span className="w-1.5 h-1.5 rounded-full bg-je-red/70" />
              </span>
            ))
          )}
        </div>
      </div>
    </section>
  )
}

export default HeroSection
